import { useState } from 'react';
import { ShieldCheck, ExternalLink, AlertCircle } from 'lucide-react';
import {
  useCurrentUser,
  usePrivacyStatus,
  useAcceptPrivacy,
  usePortal,
} from '../../hooks/useAuth';
import { Button } from '../ui/Button';
import { privacyNoticeCopy } from './privacyNoticeCopy';

/**
 * Mounts a blocking modal on first login that asks the user to
 * acknowledge the privacy notice (CLAUDE.md §10). Acceptance is
 * recorded server-side as users.privacy_accepted_at; once stamped
 * the modal never reappears for that account.
 *
 * Emergency-mode sessions bypass this gate: emergency login is the
 * break-glass path used when SSO is down, and a consent dialog
 * would block the very flow it exists to enable.
 *
 * The wording is per portal (privacyNoticeCopy): existing staff on the
 * training host are told about training records, not kit and signatures.
 * Acceptance is one flag per account regardless of where it was given.
 */
export function PrivacyNoticeGate() {
  const { data: user } = useCurrentUser();
  const { data: status, isLoading } = usePrivacyStatus();
  const accept = useAcceptPrivacy();
  const copy = privacyNoticeCopy(usePortal());
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;
  if (user.login_method === 'emergency') return null;
  if (isLoading) return null;
  if (!status || status.accepted) return null;

  async function onAccept() {
    setError(null);
    try {
      await accept.mutateAsync();
    } catch {
      setError('Could not record your acceptance. Please try again.');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/40 p-4 backdrop-blur-sm aurora-fade-in"
    >
      <div className="w-full max-w-lg overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg">
        <header className="border-b border-divider/10 bg-surface-2 px-6 py-5">
          <div className="flex items-center gap-3">
            <span
              className="flex h-10 w-10 items-center justify-center rounded-token-sm text-fg-on-accent shadow-token-accent"
              style={{
                backgroundImage:
                  'linear-gradient(135deg, rgb(var(--accent)) 0%, #ec4899 100%)',
              }}
            >
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <h2
                id="privacy-title"
                className="text-lg font-semibold tracking-tight text-fg"
              >
                Privacy notice
              </h2>
              <p className="text-xs text-fg-muted">
                One-time acknowledgement before continuing.
              </p>
            </div>
          </div>
        </header>

        <div className="space-y-3 px-6 py-5 text-sm text-fg-muted">
          <p>{copy.processing}</p>
          <p>{copy.retention}</p>
          <p>{copy.rights}</p>
          {status.policy_url ? (
            <a
              href={status.policy_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-accent underline underline-offset-2 hover:text-accent-hover"
            >
              Read the full privacy policy
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : (
            <p className="rounded-token-sm bg-warning-soft/15 px-3 py-2 text-xs text-warning-soft-fg">
              <AlertCircle className="mr-1 inline h-3.5 w-3.5" />
              The deployment hasn't been configured with a privacy policy
              URL yet. Ask an admin to set{' '}
              <code className="rounded bg-warning-soft/20 px-1">
                PRIVACY_POLICY_URL
              </code>
              .
            </p>
          )}
        </div>

        {error ? (
          <div className="mx-6 mb-4 flex items-start gap-2 rounded-token-sm bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        <footer className="flex justify-end border-t border-divider/10 bg-surface-2 px-6 py-4">
          <Button
            variant="primary"
            size="md"
            onClick={onAccept}
            loading={accept.isPending}
          >
            I understand and accept
          </Button>
        </footer>
      </div>
    </div>
  );
}
